import { ConfirmModal, MoreButton } from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { useProject } from '@/features/project/_hooks/project';
import { useDeleteSubProject } from '@/features/project/_hooks/sub-project';
import type { executionProgressType } from '@/features/project/projects/_models/execution-progress';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type Props = {
  subProjectId: string;
  tableType: z.infer<typeof executionProgressType>;
};

export const ExecutionProgressIndividualTableActions: FC<Props> = ({
  subProjectId,
  tableType,
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const { setDrawerState } = useProjectDetailDrawerState();

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const { mutateAsync: deleteSubProjectAsync } = useDeleteSubProject(projectId);

  const handleClose = async () => {
    setIsOpenDeleteModal(false);
  };

  const handleDeleteSubProject = async () => {
    try {
      await deleteSubProjectAsync(subProjectId);
      await Promise.all([
        openMessage('success', t('message-delete-completed')),
        handleClose(),
      ]);
    } catch {
      await openMessage('error', t('message-delete-failed'));
    }
  };

  return (
    <ProjectResourceGuard onlyEdit>
      {project.data && !project.data.isLocked && (
        <>
          <MoreButton
            items={[
              {
                key: 'edit-fund-info',
                label: t('edit-fund-info'),
              },
              {
                key: 'edit-income-and-expenditure-and-fund-business',
                label: t('edit-income-and-expenditure-and-fund-business'),
              },
              { key: 'delete', label: t('delete'), danger: true },
            ]}
            onClick={({ key }) => {
              switch (key) {
                case 'edit-fund-info':
                  setDrawerState({
                    open: true,
                    editSubProjectId: subProjectId,
                    type: 'execution-progress',
                    formType: 'fund-info',
                  });
                  break;
                case 'edit-income-and-expenditure-and-fund-business':
                  setDrawerState({
                    open: true,
                    type: 'execution-progress',
                    formType: 'individual-table',
                    target: tableType,
                    subProjectId,
                  });
                  break;
                case 'delete':
                  setIsOpenDeleteModal(true);
                  break;
              }
            }}
          />
          <ConfirmModal
            open={isOpenDeleteModal}
            onCancel={handleClose}
            title={t('delete-confirmation')}
            disclaimerText={t('delete-confirmation-disclaimer')}
            onOk={handleDeleteSubProject}
          />
        </>
      )}
    </ProjectResourceGuard>
  );
};
